// src/modules/ida/hooks/useMMDataBridge.ts
//
// What? PostMessage bridge that relays mmData mutations from an iframe to the backend.
// Why? HTML documents use window.mmData inside a sandboxed iframe. The iframe sends
//       postMessage to the parent, and this hook relays it as a PATCH to the API.
// How? Listens for "mmData" messages, calls the backend PATCH endpoint, sends response back.

import { useEffect, useRef } from "react";
import { getApiBaseUrl } from "../../../config/runtimeConfig";

const API_BASE_URL = getApiBaseUrl();

interface UseMMDataBridgeOptions {
  /** The document ID (used in the PATCH URL). */
  documentId: string | undefined;
  /** Origin of the dedicated content host; absent for the legacy blob preview. */
  contentOrigin?: string | null;
  /** Function that returns auth headers for API calls. */
  getAuthHeaders: () => Record<string, string>;
}

/**
 * Hook that bridges mmData postMessage calls from an iframe to the backend PATCH endpoint.
 * Returns a ref to attach to the iframe element.
 */
export function useMMDataBridge({
  documentId,
  contentOrigin,
  getAuthHeaders,
}: UseMMDataBridgeOptions) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const getAuthHeadersRef = useRef(getAuthHeaders);
  getAuthHeadersRef.current = getAuthHeaders;

  useEffect(() => {
    if (!documentId) return;

    const handleMessage = async (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (contentOrigin && event.origin !== contentOrigin) return;

      const data = event.data;
      if (!data || data.type !== "mmData") return;

      const { id, filename, op, path, value, index } = data;
      const targetDocumentId =
        typeof data.documentId === "string" && data.documentId
          ? data.documentId
          : documentId;
      if (!targetDocumentId || typeof filename !== "string") return;
      const targetOrigin = contentOrigin || "*";
      const headers = {
        "Content-Type": "application/json",
        ...getAuthHeadersRef.current(),
      };

      try {
        const resp = await fetch(
          `${API_BASE_URL}/api/public/document-data/${encodeURIComponent(targetDocumentId)}/${encodeURIComponent(filename)}`,
          {
            method: "PATCH",
            headers,
            body: JSON.stringify({ op, path, value, index }),
          },
        );

        if (!resp.ok) {
          const errBody = await resp.json().catch(() => ({}));
          const errMsg =
            (errBody as Record<string, string>).detail ||
            `Server error (${resp.status})`;
          iframeRef.current?.contentWindow?.postMessage(
            { type: "mmDataResponse", id, error: errMsg },
            targetOrigin,
          );
          return;
        }

        const result = await resp.json();
        iframeRef.current?.contentWindow?.postMessage(
          { type: "mmDataResponse", id, filename, data: result.data },
          targetOrigin,
        );
      } catch (err) {
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: "mmDataResponse",
            id,
            error: (err as Error).message,
          },
          targetOrigin,
        );
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [documentId, contentOrigin]);

  return iframeRef;
}
